/* eslint-disable react-hooks/exhaustive-deps */
import { useLocalStorage } from '@rehooks/local-storage';
import { calc } from 'csx';
import React, { useEffect, useState } from 'react';

import { LoadingOverlay, Modal } from '..';
import { usegenerateAnonymousAccessTokenMutation } from '../../apollo';
import { clearDeprecatedLocalStorage, useAnonymousUserId } from '../../hooks';

interface AnonymousRouteProps {
  children: React.ReactElement;
  policy: 'GET_TOKEN' | 'RENEW_TOKEN' | 'REQUIRE_TOKEN';
}

const AnonymousRoute: React.FC<AnonymousRouteProps> = ({ children, policy }) => {
  const [anonymousToken, setAnonymousToken] = useLocalStorage<string>('chirpAccessToken');
  const [renewingToken, setRenewingToken] = useState(policy === 'RENEW_TOKEN');

  const anonymousUserId = useAnonymousUserId();
  const [generateAnonymousToken, { error }] = usegenerateAnonymousAccessTokenMutation();

  const generateAndStoreAnonymousToken = () => {
    generateAnonymousToken().then(({ data }) => {
      if (data) {
        setAnonymousToken(data.accessToken);
        setRenewingToken(false);
        clearDeprecatedLocalStorage(); // Clean up old values in local storage
      }
    });
  };

  useEffect(() => {
    if (policy === 'GET_TOKEN' && !anonymousToken) {
      generateAndStoreAnonymousToken();
    }
  }, [policy, anonymousToken]);

  useEffect(() => {
    if (policy === 'RENEW_TOKEN') {
      generateAndStoreAnonymousToken();
    }
  }, []); // Call once

  const hasAnonymousToken = !!anonymousToken && !!anonymousUserId;

  if (policy === 'REQUIRE_TOKEN' && !hasAnonymousToken) {
    return (
      <Modal
        show
        title='Unauthorized'
        message='Please re-scan the QR code to access this page'
      />
    );
  }

  if (hasAnonymousToken && !renewingToken) {
    return (
      <div style={{
        minHeight: calc('100vh - 80px'),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}>
        {children}
      </div>
    );
  }

  if (error) { return (
    <Modal
      show
      title={error.name}
      message={`${error.message}.`}
    />
  );
  }

  return <LoadingOverlay />;
};

export default AnonymousRoute;
